// 部品区分（コントロールボックス内の部品とそれ以外を分けて管理する）
export type ComponentCategory = 'controlBox' | 'other'

export const COMPONENT_CATEGORIES: ComponentCategory[] = ['controlBox', 'other']

// 部品マスタの1エントリ（部品管理画面で全機材に対して一括登録・削除する）
export interface ComponentDef {
  name: string
  category: ComponentCategory
}

export interface MachineComponent {
  name: string
  category?: ComponentCategory
  serialNumber?: string
  note?: string
}

// ロットマスタの1エントリ（ロット管理画面で全機材共通に管理する）
export interface LotDef {
  name: string
  // 変化点などの備考（Markdown可）
  note?: string
}

export interface SoftwareVersions {
  forkRootRepos?: string
  forkMainPcEnv?: string
  forkTipSensorFw?: string
  vecowDioServer?: string
}

// ソフトバージョンの項目（キーと画面表示名）。項目を増やす場合はここに追加する
export const SOFTWARE_VERSION_FIELDS: {
  key: keyof SoftwareVersions
  label: string
}[] = [
  { key: 'forkRootRepos', label: 'fork_root_repos' },
  { key: 'forkMainPcEnv', label: 'fork_main_pc_env' },
  { key: 'forkTipSensorFw', label: 'fork_tip_sensor_fw' },
  { key: 'vecowDioServer', label: 'vecow-dio-server' },
]

export const SOFTWARE_VERSION_KEYS = SOFTWARE_VERSION_FIELDS.map((f) => f.key)

export interface MachineInput {
  // 未採番の車体やシミュレータなど、シリアルがない機材もある
  serialNumber?: string
  deviceName?: string
  bodySerial?: string
  lot?: string
  edition?: string
  customer?: string
  shippedAt?: string
  supportStatus?: string
  // 車体出荷写真・車体検査成績書へのリンク（URL）
  bodyRecord?: string
  bodyInspectionReport?: string
  remarks?: string
  // 論理削除。設定されている機材は一覧でデフォルト非表示（空文字で復元）
  deletedAt?: string
  softwareVersions?: SoftwareVersions
  components?: MachineComponent[]
  // 保存時の変更メモ（コミットメッセージ相当）。履歴一覧に表示される
  updateNote?: string
}

// 一覧の行・セルに付けられるマーカー色（Excelのセル塗りに相当）
export const MACHINE_COLORS = [
  'red',
  'orange',
  'yellow',
  'green',
  'blue',
  'purple',
  'gray',
] as const

export type MachineColor = (typeof MACHINE_COLORS)[number]

// 色の部分更新。'' はその色を消すことを表す
export interface MachineColorPatch {
  rowColor?: MachineColor | ''
  cellColors?: Record<string, MachineColor | ''>
}

// 行・セルのマーカー色の保持形。機材レコードと台帳未登録デバイスの色で共用する
export interface MachineColors {
  rowColor?: string
  cellColors?: Record<string, string>
}

// 色の部分更新を機材/デバイスの色データへ適用する（一覧の楽観的更新用。API側と同じ規則）
export function applyColorPatch<T extends MachineColors>(
  colors: T,
  patch: MachineColorPatch
): T {
  const next = { ...colors }
  if (patch.rowColor !== undefined) {
    if (patch.rowColor === '') delete next.rowColor
    else next.rowColor = patch.rowColor
  }
  if (patch.cellColors) {
    const cellColors = { ...(colors.cellColors ?? {}) }
    for (const [key, value] of Object.entries(patch.cellColors)) {
      if (value === '') delete cellColors[key]
      else cellColors[key] = value
    }
    if (Object.keys(cellColors).length > 0) next.cellColors = cellColors
    else delete next.cellColors
  }
  return next
}

export interface Machine extends MachineInput {
  machineId: string
  components: MachineComponent[]
  softwareVersions: SoftwareVersions
  // 一覧での行マーカー色（MACHINE_COLORS の値）
  rowColor?: string
  // 一覧でのセルマーカー色（列キー → MACHINE_COLORS の値）
  cellColors?: Record<string, string>
  createdAt?: string
  updatedAt?: string
  updatedBy?: string
}

// 保存で上書きされる直前の状態のスナップショット
export interface MachineRevision extends Omit<Machine, 'machineId'> {
  revisionId: string
}

export const SERIAL_PATTERN = /^[A-Za-z0-9_-]{1,32}$/

// 生産管理台帳の列構成。部品マスタ未登録時のフォールバックとして使う
export const COMPONENT_TEMPLATE: ComponentDef[] = [
  { name: '車体', category: 'other' },
  { name: 'MainPC', category: 'controlBox' },
  { name: 'AD/DAコンバータ+DIO', category: 'controlBox' },
  { name: 'SIM', category: 'controlBox' },
  { name: 'SIM LED', category: 'controlBox' },
  { name: 'BOXER PC', category: 'controlBox' },
  { name: 'BOXER SSD', category: 'controlBox' },
  { name: '配線', category: 'controlBox' },
  { name: 'LoadSW①', category: 'other' },
  { name: 'LoadSW②', category: 'other' },
  { name: 'LoadSW③', category: 'other' },
  { name: 'TireGuard', category: 'other' },
  { name: 'ForkTipSen', category: 'other' },
  { name: 'CableBackRest', category: 'other' },
  { name: 'Bumper', category: 'other' },
  { name: 'TouchPanel', category: 'other' },
  { name: '非常停止リモコン', category: 'other' },
  { name: 'TOWERLAMP', category: 'other' },
  { name: 'ForkStopper', category: 'other' },
]

const TEMPLATE_CATEGORY_BY_NAME = new Map(
  COMPONENT_TEMPLATE.map((c) => [c.name, c.category])
)

// category 未設定の既存データはテンプレートの部品名から区分を導出する
export function componentCategory(
  component: Pick<MachineComponent, 'name' | 'category'>
): ComponentCategory {
  return (
    component.category ??
    TEMPLATE_CATEGORY_BY_NAME.get(component.name) ??
    'other'
  )
}

export const EDITIONS = ['Standard', 'Lite']

// シリアル(例: 251201=25年12月の1台目)から IoT デバイス名を導出する
export function deviceNameFromSerial(serialNumber: string): string {
  if (/^\d{6}$/.test(serialNumber)) return `fork_20${serialNumber}`
  return serialNumber
}

// 当月の次のシリアル番号をサジェストする（YYMM + 月内連番2桁）
export function suggestNextSerial(existing: string[]): string {
  const now = new Date()
  const yymm = `${String(now.getFullYear()).slice(2)}${String(
    now.getMonth() + 1
  ).padStart(2, '0')}`
  const sequences = existing
    .filter((serial) => serial.startsWith(yymm) && /^\d{6}$/.test(serial))
    .map((serial) => Number(serial.slice(4)))
  const next = sequences.length > 0 ? Math.max(...sequences) + 1 : 1
  return `${yymm}${String(next).padStart(2, '0')}`
}
